/** Keys of the 3D prize art the storefront ships, the same vocabulary the backend resolves per prize. */
export const PRIZE_ART_KEYS = [
  "pizza-slice",
  "pizza-whole",
  "stuffed-crust",
  "burger",
  "fries",
  "hot-dog",
  "soda",
  "soda-bottle",
  "dessert-cake",
  "ice-cream",
  "coffee",
  "snack-coxinha",
  "sushi",
  "combo-bag",
  "free-delivery",
  "discount-tag",
  "coupon",
  "gift-box",
  "coins",
  "mystery-prize",
] as const

/** One of the shipped prize art keys. */
export type PrizeArtKey = (typeof PRIZE_ART_KEYS)[number]

const LOCAL_ART = import.meta.glob<string>("@/assets/prize-3d/*.webp", { eager: true, import: "default", query: "?url" })

/** Pixel widths the art is shipped in: the 1x source and its 2x srcset candidate. */
export const PRIZE_ART_WIDTHS = [128, 256] as const

function isPrizeArtKey(value: string): value is PrizeArtKey {
  return (PRIZE_ART_KEYS as readonly string[]).includes(value)
}

/** Resolves a raw art key: a known key stays, an unknown one becomes "mystery-prize", an absent one is `null`. */
export function normalizePrizeArtKey(key: string | null | undefined): PrizeArtKey | null {
  const trimmed = key?.trim().toLowerCase()
  if (!trimmed) return null
  return isPrizeArtKey(trimmed) ? trimmed : "mystery-prize"
}

/** Art key an older backend leaves out, inferred from the label (accent-insensitive): percent, delivery, amount, free item, else mystery. */
export function fallbackPrizeArtKey(label: string): PrizeArtKey {
  const text = label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
  if (/%|por ?cento/.test(text)) return "discount-tag"
  if (/frete|entrega|delivery/.test(text)) return "free-delivery"
  if (/r\$|\breais\b|desconto|\boff\b|cashback/.test(text)) return "coins"
  if (/gratis|brinde|\bfree\b|de graca/.test(text)) return "gift-box"
  return "mystery-prize"
}

/** The art key to draw for a prize: the backend's `key` when present (unknown values become "mystery-prize"), else the label-based default. */
export function resolvePrizeArtKey(key: string | null | undefined, label: string): PrizeArtKey {
  return normalizePrizeArtKey(key) ?? fallbackPrizeArtKey(label)
}

/** URL of the art for `key` at `width`; the single place to point at a CDN instead of the bundled files. */
export function prizeArtUrl(key: PrizeArtKey, width: (typeof PRIZE_ART_WIDTHS)[number]): string {
  return LOCAL_ART[`/src/assets/prize-3d/${key}-${width}.webp`] ?? ""
}

/** The `src` (128 px) and 2x `srcSet` for the art of `key`. */
export function prizeArtSources(key: PrizeArtKey): { src: string; srcSet: string } {
  const [one, two] = PRIZE_ART_WIDTHS
  return { src: prizeArtUrl(key, one), srcSet: `${prizeArtUrl(key, one)} 1x, ${prizeArtUrl(key, two)} 2x` }
}
